import { Component, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FlightService } from '../filghts.service';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import {
  Airport,
  UserFlightEntry,
  FlightFormValue,
} from 'src/app/shared/models/interfaces';
import { BaselineService } from '../baseline.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { ConfirmDialogComponent } from 'src/app/shared/ui-components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-flights-expansion-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    UIComponentsModule,
    MatTooltipModule,
  ],
  templateUrl: './flights.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './flights.component.scss',
})
export class FlightsComponent implements OnInit {
  readonly flightForm = this.initFlightForm();
  readonly cabinClassOptions = this.flightService.getCabinOptions();
  readonly hasAnsweredFlights: WritableSignal<boolean> = signal(false);

  filteredDepartureAirports: Airport[] = [];
  filteredDestinationAirports: Airport[] = [];
  flights: UserFlightEntry[] = [];
  didNotFly = false;

  destroy$ = new Subject<void>();

  constructor(
    private flightService: FlightService,
    private BaselineService: BaselineService,
    private snackBarService: SnackbarService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.BaselineService.getUserFlightEntries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (flights) => {
          // Display the flights, hide the did not fly entry
          this.flights = Array.isArray(flights) ? flights : [flights];
          this.didNotFly = this.flights.some(
            (flight) => flight.departureAirport === ''
          );
          this.hasAnsweredFlights.set(this.flights.length > 0);
          this.flights = this.flights.filter(
            (flight) => flight.departureAirport !== ''
          );

          // Disable form if didNotFly is true
          if (this.didNotFly) {
            this.clearAndDisableForm();
          }
        },
        error: (err) => {
          console.error('Error fetching flight entries:', err);
          this.snackBarService.showErrorMessage(
            'Failed to fetch flight entries.'
          );
        },
      });

    this.flightForm
      .get('departureAirport')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => this.flightService.searchAirports(value ?? '')),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => (this.filteredDepartureAirports = results));

    this.flightForm
      .get('destinationAirport')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => this.flightService.searchAirports(value ?? '')),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => (this.filteredDestinationAirports = results));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initFlightForm(): FormGroup {
    return new FormGroup({
      departureAirport: new FormControl<Airport | null>(
        null,
        Validators.required
      ),
      destinationAirport: new FormControl<Airport | null>(
        null,
        Validators.required
      ),
      cabinClass: new FormControl('', Validators.required),
      causedEmissions: new FormControl(0),
    });
  }

  addFlight(): void {
    if (this.flightForm.invalid) return;

    const formValues = this.flightForm.value as FlightFormValue;
    if (!this.isFormComplete(formValues)) {
      this.showFormError();
      return;
    }

    const { departureAirport, destinationAirport, cabinClass } = formValues;

    if (!departureAirport || !destinationAirport) {
      this.showFormError();
      return;
    }

    this.fetchFlightFootprint(
      departureAirport.iata,
      destinationAirport.iata,
      cabinClass as 'economy' | 'premium_economy' | 'business' | 'first'
    );

    // Reset the form
    this.flightForm.reset();
  }

  private isFormComplete(values: FlightFormValue): boolean {
    return !!(
      values.departureAirport &&
      values.destinationAirport &&
      values.cabinClass
    );
  }

  private showFormError(): void {
    this.snackBarService.showErrorMessage(
      'Please select departure, destination, and cabin class.'
    );
  }

  private fetchFlightFootprint(
    departureIata: string,
    destinationIata: string,
    cabinClass: 'economy' | 'premium_economy' | 'business' | 'first'
  ): void {
    this.flightService
      .getFlightFootprint(
        [
          {
            departureAirport: departureIata,
            destinationAirport: destinationIata,
          },
        ],
        cabinClass
      )
      .subscribe({
        next: (response) =>
          this.handleFlightFootprintSuccess(
            departureIata,
            destinationIata,
            cabinClass,
            response.footprint
          ),
        error: (err) => this.handleFootprintError(err),
      });
  }

  onDidNotFlyChange(event: any): void {
    this.didNotFly = event.checked;

    if (event.checked) {
      // Clear and disable the form when "did not fly" is checked
      this.clearAndDisableForm();

      if (this.hasFlights()) {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: 'Confirm Removal',
              message:
                'You have already added flights. Are you sure you want to remove all flights?' +
                ' This action cannot be undone.',
              confirmButtonText: 'Yes, remove all flights',
              cancelButtonText: 'No, keep my flights',
            },
          })
          .afterClosed()
          .subscribe((confirm: boolean) => {
            if (confirm) {
              this.createDidNotFlyEntry();
              // Clear the flights array and update the signal
              this.flights = [];
              this.hasAnsweredFlights.set(true);
            } else {
              this.didNotFly = false;
              // Reset the checkbox if the user cancels
              event.source.checked = false;
              this.hasAnsweredFlights.set(false);
              this.enableForm();
            }
          });
      } else {
        this.hasAnsweredFlights.set(true);
        this.createDidNotFlyEntry();
      }
    } else {
      this.hasAnsweredFlights.set(false);
      this.deleteDidNotFlyEntry();
      this.enableForm();
    }
  }

  private clearAndDisableForm(): void {
    this.flightForm.reset();
    this.flightForm.disable();
  }

  private enableForm(): void {
    this.flightForm.enable();
  }

  private handleFlightFootprintSuccess(
    departureIata: string,
    destinationIata: string,
    cabinClass: string,
    emissions: number
  ): void {
    const flightEntry = {
      departureAirport: departureIata,
      destinationAirport: destinationIata,
      cabinClass,
      causedEmissions: emissions,
    };

    this.flights.push(flightEntry);
    this.hasAnsweredFlights.set(true);
    this.flightForm.reset();

    // Clear any validation errors by marking form as untouched and pristine
    this.flightForm.markAsUntouched();
    this.flightForm.markAsPristine();

    this.saveFlightEntry(flightEntry);
    this.BaselineService.deleteDidNotFlyEntry().subscribe({
      error: (err) => {
        console.error('Error deleting did not fly entry:', err);
      },
    });
  }

  private saveFlightEntry(entry: UserFlightEntry): void {
    this.BaselineService.createUserFlightEntry(entry).subscribe({
      next: () => this.refreshFlightEntries(),
      error: () => alert('Failed to submit flights'),
    });
  }

  private refreshFlightEntries(): void {
    this.BaselineService.getUserFlightEntries().subscribe({
      next: (flights) => {
        this.flights = Array.isArray(flights) ? flights : [flights];
        this.hasAnsweredFlights.set(this.flights.length > 0);
      },
    });
  }

  private handleFootprintError(error: any): void {
    const message =
      error.status === 404
        ? 'Currently we cannot calculate the footprint for the airports you selected, please try again with closeby, larger airports.'
        : 'Could not calculate flight emissions, please try again.';

    this.snackBarService.showErrorMessage(message);
    console.error('Error from backend flight footprint:', error);
  }

  removeFlight(index: number): void {
    if (this.flights[index].id) {
      this.BaselineService.deleteUserFlightEntry(this.flights[index]).subscribe(
        {
          next: () => {
            this.snackBarService.showSuccessMessage(
              'Flight entry deleted successfully.'
            );
          },
          error: () => {
            this.snackBarService.showErrorMessage(
              'Failed to delete flight entry.'
            );
          },
        }
      );
    }
    this.flights.splice(index, 1);
    this.hasAnsweredFlights.set(this.flights.length > 0);
  }

  createDidNotFlyEntry(): void {
    this.BaselineService.createDidNotFlyEntry().subscribe({
      next: () => {},
      error: () => {
        this.snackBarService.showErrorMessage(
          'Failed to create did not fly entry.'
        );
      },
    });
  }

  deleteDidNotFlyEntry(): void {
    this.BaselineService.deleteDidNotFlyEntry().subscribe({
      error: (err) => {
        console.error('Error deleting did not fly entry:', err);
      },
    });
  }

  hasFlights(): boolean {
    // Check if there are any flights that are not didNotFly
    return this.flights.length > 0 && this.flights[0].departureAirport !== '';
  }

  displayAirport = (airport: Airport | string): string => {
    return this.flightService.airportDisplay(airport);
  };
}
